import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Avatar,
  Divider,
  Alert,
  CircularProgress,
  IconButton,
  Collapse
} from '@mui/material';
import { Reply, Delete, ExpandMore, ExpandLess } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { commentService } from '../services/api';
import ProfanityAwareTextField from './ProfanityAwareTextField';

const Comment = ({ comment, storyId, onReply, onDelete, level = 0 }) => {
  const { user } = useAuth();
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [showReplies, setShowReplies] = useState(false);
  const [replies, setReplies] = useState([]);
  const [loadingReplies, setLoadingReplies] = useState(false);
  const [replyContent, setReplyContent] = useState('');
  const [submittingReply, setSubmittingReply] = useState(false);
  const [isReplyValid, setIsReplyValid] = useState(true);

  const canDelete = user && (user.sub === comment.author_id || user.role === 'admin');
  const hasReplies = comment.reply_count > 0;

  const getAuthorName = (comment) => {
    if (comment.author) {
      const name = `${comment.author.given_name || ''} ${comment.author.family_name || ''}`.trim();
      if (name) return name;
    }
    return 'Anonymous';
  };

  const loadReplies = async () => {
    if (replies.length > 0) {
      setShowReplies(!showReplies);
      return;
    }

    try {
      setLoadingReplies(true);
      const response = await commentService.getReplies(storyId, comment.comment_id);
      const repliesData = response.data?.data?.comments || response.data?.comments || [];
      setReplies(repliesData);
      setShowReplies(true);
    } catch (error) {
      console.error('Error loading replies:', error);
    } finally {
      setLoadingReplies(false);
    }
  };

  const handleReplySubmit = async () => {
    if (!replyContent.trim()) return;

    try {
      setSubmittingReply(true);
      const response = await commentService.createComment(storyId, {
        content: replyContent.trim(),
        parent_comment_id: comment.comment_id
      });

      const newReply = response.data?.data || response.data;
      setReplies([...replies, newReply]);
      setReplyContent('');
      setShowReplyForm(false);
      setShowReplies(true);
      
      // Update parent comment reply count
      comment.reply_count = (comment.reply_count || 0) + 1;
      
      if (onReply) onReply();
    } catch (error) {
      console.error('Error submitting reply:', error);
    } finally {
      setSubmittingReply(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this comment?')) return;

    try {
      await commentService.deleteComment(storyId, comment.comment_id);
      if (onDelete) onDelete(comment.comment_id);
    } catch (error) {
      console.error('Error deleting comment:', error);
    }
  };

  return (
    <Box sx={{ ml: level * 3, mb: 2 }}>
      <Paper elevation={1} sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <Avatar sx={{ width: 32, height: 32, fontSize: '0.875rem' }}>
            {getAuthorName(comment).charAt(0).toUpperCase()}
          </Avatar>
          
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {getAuthorName(comment)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {new Date(comment.created_at).toLocaleString()}
                </Typography>
              </Box>
              
              {canDelete && (
                <IconButton size="small" onClick={handleDelete} color="error">
                  <Delete fontSize="small" />
                </IconButton>
              )}
            </Box>
            
            <Typography variant="body2" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
              {comment.content}
            </Typography>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {user && (
                <Button
                  size="small"
                  startIcon={<Reply />}
                  onClick={() => setShowReplyForm(!showReplyForm)}
                >
                  Reply
                </Button>
              )}
              
              {hasReplies && (
                <Button
                  size="small"
                  startIcon={loadingReplies ? <CircularProgress size={16} /> : (showReplies ? <ExpandLess /> : <ExpandMore />)}
                  onClick={loadReplies}
                  disabled={loadingReplies}
                >
                  {showReplies ? 'Hide' : 'Show'} {comment.reply_count} {comment.reply_count === 1 ? 'reply' : 'replies'}
                </Button>
              )}
            </Box>
          </Box>
        </Box>
        
        <Collapse in={showReplyForm}>
          <Box sx={{ mt: 2, ml: 5 }}>
            <ProfanityAwareTextField
              fullWidth
              multiline
              rows={2}
              placeholder="Write a reply..."
              value={replyContent}
              onChange={(e) => setReplyContent(e.target.value)}
              onValidationChange={(isValid) => setIsReplyValid(isValid)}
              showProfanityWarning={true}
              showCleanSuggestion={true}
              realTimeValidation={true}
              sx={{ mb: 1 }}
            />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                variant="contained"
                onClick={handleReplySubmit}
                disabled={!replyContent.trim() || submittingReply || !isReplyValid}
              >
                {submittingReply ? <CircularProgress size={16} /> : 'Reply'}
              </Button>
              <Button
                size="small"
                onClick={() => {
                  setShowReplyForm(false);
                  setReplyContent('');
                  setIsReplyValid(true); // Reset validation state
                }}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        </Collapse>
      </Paper>
      
      <Collapse in={showReplies}>
        <Box sx={{ mt: 1 }}>
          {replies.map((reply) => (
            <Comment
              key={reply.comment_id}
              comment={reply}
              storyId={storyId}
              onReply={onReply}
              onDelete={(deletedId) => {
                setReplies(replies.filter(r => r.comment_id !== deletedId));
                comment.reply_count = Math.max(0, (comment.reply_count || 0) - 1);
              }}
              level={level + 1}
            />
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

const Comments = ({ storyId }) => {
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [isCommentValid, setIsCommentValid] = useState(true);

  const fetchComments = async () => {
    try {
      setLoading(true);
      const response = await commentService.getComments(storyId);
      const commentsData = response.data?.data?.comments || response.data?.comments || [];
      
      // Filter out replies (they'll be loaded separately)
      const topLevelComments = commentsData.filter(comment => !comment.parent_id);
      setComments(topLevelComments);
    } catch (error) {
      console.error('Error fetching comments:', error);
      setError('Failed to load comments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (storyId) {
      fetchComments();
    }
  }, [storyId]);

  const handleSubmitComment = async () => {
    if (!newComment.trim()) return;

    try {
      setSubmitting(true);
      const response = await commentService.createComment(storyId, {
        content: newComment.trim()
      });

      const comment = response.data?.data || response.data;
      setComments([comment, ...comments]);
      setNewComment('');
    } catch (error) {
      console.error('Error submitting comment:', error);
      setError('Failed to submit comment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteComment = (deletedId) => {
    setComments(comments.filter(comment => comment.comment_id !== deletedId));
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h5" gutterBottom>
        Comments ({comments.length})
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {user ? (
        <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
          <ProfanityAwareTextField
            fullWidth
            multiline
            rows={3}
            placeholder="Write a comment..."
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            onValidationChange={(isValid) => setIsCommentValid(isValid)}
            showProfanityWarning={true}
            showCleanSuggestion={true}
            realTimeValidation={true}
            sx={{ mb: 2 }}
          />
          <Button
            variant="contained"
            onClick={handleSubmitComment}
            disabled={!newComment.trim() || submitting || !isCommentValid}
          >
            {submitting ? <CircularProgress size={20} /> : 'Post Comment'}
          </Button>
        </Paper>
      ) : (
        <Alert severity="info" sx={{ mb: 3 }}>
          Please sign in to leave a comment.
        </Alert>
      )}
      
      <Divider sx={{ mb: 3 }} />
      
      {comments.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
          No comments yet. Be the first to comment!
        </Typography>
      ) : (
        <Box>
          {comments.map((comment) => (
            <Comment
              key={comment.comment_id}
              comment={comment}
              storyId={storyId}
              onReply={fetchComments}
              onDelete={handleDeleteComment}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default Comments;
